import React, { useState, useMemo } from "react";

// @mui material components
import Card from "@mui/material/Card";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableContainer from "@mui/material/TableContainer";
import TableRow from "@mui/material/TableRow";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Data helpers
import dataBuilder, { buildStoreRow } from "layouts/dashboard/components/Projects/data";
import DataTable from "examples/Tables/DataTable";
import DataTableHeadCell from "examples/Tables/DataTable/DataTableHeadCell";
import DataTableBodyCell from "examples/Tables/DataTable/DataTableBodyCell";
import { NoDataPage } from "examples/Widgets/NoDataPage";
import tooltipIcon from "assets/images/icons/catering-icons/tooltip-icon.svg";
import formatNumberWithCommas from "../../../../core/utils/formatNumberWithCommas";
import "examples/Tables/table.css";

import PropTypes from "prop-types";

function Projects({ topRestaurantData, brandData }) {
  const [expandedBrands, setExpandedBrands] = useState({});

  const toggleBrand = (brandId) => {
    setExpandedBrands((prev) => ({ ...prev, [brandId]: !prev[brandId] }));
  };

  // Group stores under their brands and identify standalone stores
  const { brandRows, standaloneStores } = useMemo(() => {
    if (!brandData || brandData.length === 0) {
      return { brandRows: [], standaloneStores: topRestaurantData || [] };
    }

    const brandStoreIdSet = new Set();
    brandData.forEach((b) => b.storeIds?.forEach((id) => brandStoreIdSet.add(id)));

    const standalone = (topRestaurantData || []).filter(
      (item) => !brandStoreIdSet.has(item._restaurant_store_restaurant_store?.id)
    );

    const brands = brandData.map((brand) => {
      const childStores = (topRestaurantData || []).filter((item) =>
        brand.storeIds?.includes(item._restaurant_store_restaurant_store?.id)
      );
      return { ...brand, childStores };
    });

    return { brandRows: brands, standaloneStores: standalone };
  }, [topRestaurantData, brandData]);

  const hasBrands = brandRows.length > 0;
  const hasData = hasBrands || standaloneStores.length > 0;

  const columns = [
    { Header: "RESTAURANT NAME", width: "20%", align: "left" },
    { Header: "LOCATION", align: "left" },
    { Header: "ORDERS SINCE ANNIVERSARY", align: "center" },
    { Header: "ANNIVERSARY DATE", align: "center" },
    {
      Header: (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          MAX CASHBACK
          <Tooltip title="Max Cashback is based on your current tier" placement="bottom">
            <img src={tooltipIcon} alt="info" style={{ width: 15, height: 15 }} />
          </Tooltip>
        </span>
      ),
      align: "center",
    },
    { Header: "CURRENT TIER", align: "left" },
  ];

  // If no brands, use the existing flat DataTable for backwards compatibility
  if (!hasBrands) {
    const { columns: flatColumns, rows } = dataBuilder({ topRestaurantData });
    return (
      <Card>
        <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
          <MDBox>
            <MDTypography variant="h6" gutterBottom>
              My Restaurants
            </MDTypography>
          </MDBox>
        </MDBox>
        <MDBox>
          <DataTable
            table={{ columns: flatColumns, rows }}
            showTotalEntries={false}
            isSorted={false}
            noEndBorder
            entriesPerPage={false}
          />
        </MDBox>
      </Card>
    );
  }

  const renderBrandRow = (brand) => {
    const isExpanded = expandedBrands[brand.brandId];
    const storeCount = brand.childStores?.length || 0;

    // Aggregate total spend from child stores
    const totalSpend =
      brand.childStores?.reduce((sum, s) => sum + (s.totalSpendSinceAnniversary || 0), 0) || 0;

    return (
      <MDBox
        role="button"
        tabIndex={0}
        aria-expanded={isExpanded}
        onClick={() => toggleBrand(brand.brandId)}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleBrand(brand.brandId);
          }
        }}
        py={2}
        sx={{
          cursor: "pointer",
          px: { xs: 2, sm: 3 },
          backgroundColor: isExpanded ? "#F0FAF4" : "#FAFAFA",
          "&:hover": { backgroundColor: isExpanded ? "#E0F2E8" : "#F3F4F6" },
          borderBottom: isExpanded ? "1px solid #E5E7EB" : "none",
          transition: "background-color 0.2s ease",
        }}
      >
        <MDBox display="flex" alignItems="center" flexWrap="wrap">
          <Icon
            sx={{
              fontSize: 18,
              color: "#374151",
              mr: 1,
              transition: "transform 0.2s",
              transform: isExpanded ? "rotate(90deg)" : "rotate(0deg)",
            }}
          >
            chevron_right
          </Icon>
          <MDTypography
            variant="button"
            fontWeight="bold"
            sx={{ fontSize: "15px", color: "#111827", textTransform: "none" }}
          >
            {brand.brandName}
          </MDTypography>
          {storeCount > 0 && (
            <MDBox
              component="span"
              sx={{
                ml: 1.5,
                backgroundColor: "#E8F5E9",
                color: "#238856",
                borderRadius: "12px",
                padding: "2px 8px",
                fontSize: "11px",
                fontWeight: 600,
                lineHeight: 1.4,
              }}
            >
              {storeCount} {storeCount === 1 ? "store" : "stores"}
            </MDBox>
          )}
          {totalSpend > 0 && (
            <>
              <MDTypography component="span" variant="caption" color="secondary" sx={{ mx: 1 }}>
                ·
              </MDTypography>
              <MDTypography variant="caption" color="text" fontWeight="medium">
                ${formatNumberWithCommas(totalSpend / 100, { asCurrency: true })} total spend
              </MDTypography>
            </>
          )}
        </MDBox>
      </MDBox>
    );
  };

  const renderChildStoreRow = (storeData, isLast) => {
    const row = buildStoreRow(storeData);
    if (!row) return null;
    return (
      <TableRow
        key={`store-${storeData._restaurant_store_restaurant_store?.id}`}
        sx={{ backgroundColor: "#FFFFFF" }}
        className="box-shodow"
      >
        <DataTableBodyCell align="left" noBorder={isLast} dataLabel="RESTAURANT NAME">
          <MDBox sx={{ pl: { xs: 0, md: 1.5 } }}>{row.restaurantName}</MDBox>
        </DataTableBodyCell>
        <DataTableBodyCell align="left" noBorder={isLast} dataLabel="LOCATION">
          {row.location}
        </DataTableBodyCell>
        <DataTableBodyCell align="center" noBorder={isLast} dataLabel="ORDERS SINCE ANNIVERSARY">
          {row.last12Months}
        </DataTableBodyCell>
        <DataTableBodyCell align="center" noBorder={isLast} dataLabel="ANNIVERSARY DATE">
          {row.anniversaryDate}
        </DataTableBodyCell>
        <DataTableBodyCell align="center" noBorder={isLast} dataLabel="MAX CASHBACK">
          {row.maxCashback}
        </DataTableBodyCell>
        <DataTableBodyCell align="left" noBorder={isLast} dataLabel="CURRENT TIER">
          {row.progress}
        </DataTableBodyCell>
      </TableRow>
    );
  };

  const renderHeaderRow = () => (
    <TableRow className="table-header-row">
      {columns.map((col, idx) => (
        <DataTableHeadCell
          key={idx}
          width={col.width || "auto"}
          align={col.align || "left"}
          sorted={false}
        >
          {col.Header}
        </DataTableHeadCell>
      ))}
    </TableRow>
  );

  return (
    <Card>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" p={2}>
        <MDBox>
          <MDTypography variant="h6" gutterBottom>
            My Locations
          </MDTypography>
        </MDBox>
      </MDBox>
      <MDBox px={2} pb={2}>
        {/* Each brand in its own card */}
        {brandRows.map((brand) => (
          <MDBox
            key={brand.brandId}
            mb={2}
            sx={{
              border: "1px solid #E5E7EB",
              borderLeft: expandedBrands[brand.brandId]
                ? "3px solid #238856"
                : "1px solid #E5E7EB",
              borderRadius: "8px",
              overflow: "hidden",
              transition: "border-left 0.2s ease",
            }}
          >
            {renderBrandRow(brand)}
            {expandedBrands[brand.brandId] && (
              <TableContainer sx={{ boxShadow: "none", pt: 1 }}>
                <div className="table-responsive">
                  <Table className="custom-table-project">
                    <TableBody>
                      {brand.childStores.length > 0 && renderHeaderRow()}
                      {brand.childStores.map((store, idx) =>
                        renderChildStoreRow(store, idx === brand.childStores.length - 1)
                      )}
                      {brand.childStores.length === 0 && (
                        <TableRow>
                          <MDBox
                            component="td"
                            colSpan={6}
                            py={2}
                            px={2}
                            sx={{ textAlign: "center" }}
                          >
                            <MDTypography variant="caption" color="secondary" fontStyle="italic">
                              No orders yet for this brand — place your first order to start earning
                              rewards!
                            </MDTypography>
                          </MDBox>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </div>
              </TableContainer>
            )}
          </MDBox>
        ))}

        {/* Standalone stores not belonging to any brand */}
        {standaloneStores.length > 0 && (
          <MDBox
            mb={2}
            sx={{
              border: "1px solid #E5E7EB",
              borderLeft: "3px solid #238856",
              borderRadius: "8px",
              overflow: "hidden",
            }}
          >
            <MDBox
              py={2}
              sx={{
                px: { xs: 2, sm: 3 },
                backgroundColor: "#FAFAFA",
                borderBottom: "1px solid #E5E7EB",
              }}
            >
              <MDTypography
                variant="button"
                fontWeight="bold"
                sx={{ fontSize: "15px", color: "#111827", textTransform: "none" }}
              >
                Other Restaurants
              </MDTypography>
            </MDBox>
            <TableContainer sx={{ boxShadow: "none", pt: 1 }}>
              <div className="table-responsive">
                <Table className="custom-table-project">
                  <TableBody>
                    {renderHeaderRow()}
                    {standaloneStores.map((store, idx) =>
                      renderChildStoreRow(store, idx === standaloneStores.length - 1)
                    )}
                  </TableBody>
                </Table>
              </div>
            </TableContainer>
          </MDBox>
        )}

        {!hasData && (
          <MDBox>
            <NoDataPage />
          </MDBox>
        )}
      </MDBox>
    </Card>
  );
}

Projects.propTypes = {
  topRestaurantData: PropTypes.any,
  brandData: PropTypes.array,
};

Projects.defaultProps = {
  brandData: [],
};

export default Projects;
